// Timezone-safe date helpers.
//
// The core bug this exists to prevent: `new Date("2026-01-11")` in JavaScript
// parses the string as UTC midnight. In any timezone west of UTC (US, most of
// the app's audience) that becomes the *previous* day (Saturday Jan 10) once
// converted to local time. Every "day of week" or "MMM D" render then reads a
// day off from what the user intended.
//
// Rule of thumb:
//   • Bare "YYYY-MM-DD"  → treat as a *civil* date, no time zone. Parse locally.
//   • Full ISO with time (has 'T') → the caller cares about wall-clock time.
//     Leave as-is so `2026-01-11T22:00:00Z` still renders the right hour.
//
// Every date formatter in the app should go through `parseLocalDate` or the
// convenience `formatLocalDate` wrapper.

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/** Parse an incoming date value into a Date without unwanted UTC shift. */
export const parseLocalDate = (input) => {
  if (input instanceof Date) return input;
  if (typeof input !== 'string' || !input) return new Date(NaN);
  if (DATE_ONLY.test(input)) {
    const [y, m, d] = input.split('-').map(Number);
    return new Date(y, m - 1, d);
  }
  return new Date(input);
};

/**
 * Format a date value using Intl options — always safe against the
 * YYYY-MM-DD → UTC → yesterday bug.
 *
 *   formatLocalDate('2026-01-11')                  // "January 11, 2026"
 *   formatLocalDate(iso, { month: 'short', day: 'numeric' })
 *   formatLocalDate(prayer.created_at, 'relative') // "3h ago"
 */
export const formatLocalDate = (input, opts) => {
  const date = parseLocalDate(input);
  if (Number.isNaN(date.getTime())) return '';

  if (opts === 'relative') return formatRelative(date);

  const options = opts || { month: 'long', day: 'numeric', year: 'numeric' };
  return date.toLocaleDateString('en-US', options);
};

/** "Just now / 3m ago / 5h ago / 2d ago / Feb 12" style. */
export const formatRelative = (input) => {
  const date = parseLocalDate(input);
  if (Number.isNaN(date.getTime())) return '';
  const diffMs = Date.now() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

/** Today formatted as "YYYY-MM-DD" in the LOCAL timezone (not UTC). */
export const localTodayISO = (d = new Date()) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
};
